import React, { useState } from 'react';
import { Check, Pencil, Plus, Trash2, X } from 'lucide-react';

/**
 * 줄마다 고치고 지우는 표 — 투자처 상세 탭(연혁 · 라운드 · 주주 · 재무 …)이 같이 쓴다.
 *
 * 저장은 부르는 쪽이 한다(onSave · onRemove). 값의 옳고 그름은 DB CHECK 가 막고, 막히면 그 말을 띄운다.
 * 지우기는 두 번 눌러야 한다 — 원장과 같다.
 */
export interface Col<T> {
  key: keyof T & string;
  label: string;
  type?: 'text' | 'date' | 'number' | 'money' | 'select';
  options?: readonly (readonly [string, string])[];
  right?: boolean;
  /** 보기 모드 표시 — 없으면 값 그대로(금액은 억 · 만) */
  show?: (r: T) => React.ReactNode;
  required?: boolean;
  width?: string;
}

interface Props<T extends { id: number }> {
  rows: T[];
  cols: Col<T>[];
  blank: () => Partial<T>;
  onSave: (id: number | null, v: Partial<T>) => Promise<unknown>;
  onRemove: (id: number) => Promise<unknown>;
  onDone: () => void;
  empty?: string;
  /** 계산 칸 — 보기 모드에서 줄 끝에 붙는다 */
  tail?: { head: string[]; cells: (r: T) => React.ReactNode[] };
  rowClass?: (r: T) => string;
  addLabel?: string;
}

export const money = (v: number | null | undefined): string => {
  if (v === null || v === undefined) return '—';
  const a = Math.abs(v);
  // 10억 미만은 소수 둘째 자리(3.41억) — shortWon 과 같은 규칙(2026-11-02)
  const s = a >= 100_000_000 ? `${Number((a / 100_000_000).toFixed(a < 1_000_000_000 ? 2 : 1)).toLocaleString()}억` : a >= 10_000 ? `${Math.round(a / 10_000).toLocaleString()}만` : a.toLocaleString();
  return v < 0 ? `−${s}` : s;
};
const dot = (s: string | null | undefined) => (s ? s.replace(/-/g, '.') : '—');

export function EditTable<T extends { id: number }>({ rows, cols, blank, onSave, onRemove, onDone, empty = '없음', tail, rowClass, addLabel = '추가' }: Props<T>) {
  const [edit, setEdit] = useState<{ id: number | null; v: Record<string, any> } | null>(null);
  const [armed, setArmed] = useState<number | null>(null);
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState(false);

  const start = (id: number | null, v: Record<string, any>) => { setErr(''); setArmed(null); setEdit({ id, v: { ...v } }); };
  const save = async () => {
    if (!edit) return;
    const miss = cols.find((c) => c.required && (edit.v[c.key] === '' || edit.v[c.key] == null));
    if (miss) { setErr(`${miss.label} 비어 있음`); return; }
    const out: Record<string, any> = {};
    for (const c of cols) {
      const v = edit.v[c.key];
      out[c.key] = (c.type === 'number' || c.type === 'money') ? (v === '' || v == null ? null : Number(v)) : v === '' ? null : v;
    }
    setBusy(true);
    try { await onSave(edit.id, out as Partial<T>); setEdit(null); onDone(); } catch (e) { setErr((e as Error).message); } finally { setBusy(false); }
  };
  const remove = async (id: number) => {
    if (armed !== id) { setArmed(id); return; }
    try { await onRemove(id); setArmed(null); onDone(); } catch (e) { setErr((e as Error).message); }
  };

  const view = (c: Col<T>, r: T) => {
    if (c.show) return c.show(r);
    const v = r[c.key] as unknown;
    if (c.type === 'money') return money(v as number | null);
    if (c.type === 'date') return dot(v as string | null);
    if (c.type === 'number') return v == null ? '—' : Number(v).toLocaleString();
    if (c.type === 'select') return c.options?.find(([k]) => k === v)?.[1] ?? (v == null ? '—' : String(v));
    return v == null || v === '' ? '—' : String(v);
  };
  const input = (c: Col<T>) => {
    const v = edit!.v[c.key] ?? '';
    const set = (x: unknown) => setEdit((e) => e && { ...e, v: { ...e.v, [c.key]: x } });
    const cls = 'w-full rounded border border-slate-200 px-1.5 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-200';
    if (c.type === 'select') {
      return (
        <select aria-label={c.label} value={v} onChange={(e) => set(e.target.value)} className={cls}>
          {!c.required && <option value="">—</option>}
          {c.options!.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
        </select>
      );
    }
    return (
      <input aria-label={c.label} type={c.type === 'date' ? 'date' : c.type === 'number' || c.type === 'money' ? 'number' : 'text'}
        value={v} onChange={(e) => set(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') save(); if (e.key === 'Escape') setEdit(null); }}
        className={`${cls} ${c.right ? 'text-right tabular-nums' : ''}`} />
    );
  };

  const editRow = () => (
    <tr className="border-b border-slate-50 bg-indigo-50/40">
      {cols.map((c) => <td key={c.key} className="px-2 py-1.5" style={{ width: c.width }}>{input(c)}</td>)}
      {tail?.head.map((h) => <td key={h} />)}
      <td className="px-2 py-1.5 whitespace-nowrap text-right">
        <button aria-label="저장" disabled={busy} onClick={save} className="p-1 rounded text-indigo-600 hover:bg-indigo-100"><Check size={14} /></button>
        <button aria-label="취소" onClick={() => setEdit(null)} className="p-1 rounded text-slate-400 hover:bg-slate-100"><X size={14} /></button>
      </td>
    </tr>
  );

  return (
    <div>
      {(rows.length > 0 || edit !== null) && <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-xs text-slate-400 border-b border-slate-100">
              {cols.map((c, i) => <th key={c.key} className={`font-medium py-2 ${i === 0 ? 'pl-4 pr-2' : 'px-2'} ${c.right ? 'text-right' : 'text-left'}`}>{c.label}</th>)}
              {tail?.head.map((h) => <th key={h} className="font-medium px-2 py-2 text-right">{h}</th>)}
              <th className="w-16" />
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (edit?.id === r.id ? <React.Fragment key={r.id}>{editRow()}</React.Fragment> : (
              <tr key={r.id} className={`group border-b border-slate-50 last:border-b-0 ${rowClass?.(r) ?? ''}`}>
                {cols.map((c, i) => (
                  <td key={c.key} className={`py-1.5 ${i === 0 ? 'pl-4 pr-2' : 'px-2'} ${c.right ? 'text-right tabular-nums whitespace-nowrap' : ''} ${
                    (c.type === 'money' || c.type === 'number') && Number(r[c.key]) < 0 ? 'text-red-600' : ''}`}>{view(c, r)}</td>
                ))}
                {tail?.cells(r).map((x, i) => <td key={i} className="px-2 py-1.5 text-right tabular-nums whitespace-nowrap">{x}</td>)}
                <td className="px-2 py-1.5 whitespace-nowrap text-right">
                  <span className="opacity-0 group-hover:opacity-100 focus-within:opacity-100">
                    <button aria-label="고치기" onClick={() => start(r.id, r as Record<string, any>)} className="p-1 rounded text-slate-400 hover:text-slate-700 hover:bg-slate-100"><Pencil size={13} /></button>
                  </span>
                  <button aria-label="지우기" onClick={() => remove(r.id)}
                    className={`p-1 rounded ${armed === r.id ? 'text-red-600 bg-red-50' : 'text-slate-400 opacity-0 group-hover:opacity-100 hover:text-red-600'}`}>
                    <Trash2 size={13} />
                  </button>
                </td>
              </tr>
            )))}
            {edit?.id === null && editRow()}
          </tbody>
        </table>
      </div>}
      {rows.length === 0 && edit === null && empty && <p className="px-4 py-2 text-sm text-slate-400">{empty}</p>}
      {err && <p className="px-4 py-1 text-xs text-red-600">{err}</p>}
      {edit?.id !== null && (
        <button onClick={() => start(null, blank() as Record<string, any>)}
          className="ml-3 my-1.5 inline-flex items-center gap-1 px-2 py-1 rounded text-xs font-medium text-indigo-600 hover:bg-indigo-50">
          <Plus size={12} /> {addLabel}
        </button>
      )}
    </div>
  );
}
